import React from "react";
import FontAwesome from "react-fontawesome";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
	SortableContext,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { RowGripIcon } from "./RowGripIcon";
import { RowActionsMenu } from "./RowActionsMenu";
import { ROOT_PARENT } from "../utilities/dragUtils";

/** @typedef {import("../types").InfoNode} InfoNode */

/**
 * Renders a folder row that is both sortable within the root list and hosts
 * its own sortable context for child pages.
 * @param {{
 *   node: InfoNode,
 *   childIds: string[],
 *   canAdmin: boolean,
 *   onDeleteNode: () => void,
 *   onEditFolder: () => void,
 *   children: React.ReactNode,
 *   isCollapsed: boolean,
 *   onToggleCollapse: () => void,
 *   onCreateChildPage?: (parentId: string) => void,
 *   isNestTarget?: boolean,
 * }} props
 */
export function DroppableFolder({
	node,
	childIds,
	canAdmin,
	onDeleteNode,
	onEditFolder,
	onCreateChildPage,
	children,
	isCollapsed,
	onToggleCollapse,
	isNestTarget = false,
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
		isOver,
	} = useSortable({
		id: node.RowId,
		data: {
			acceptsPage: true,
			nodeType: "folder",
			parentId: ROOT_PARENT,
			containerId: node.RowId,
			isEmpty: childIds.length === 0,
		},
	});
	const nestDropZoneId = `nest-${node.RowId}`;
	// Dedicated inner drop zone to distinguish nesting from reordering.
	const { isOver: isOverNestState, setNodeRef: setNestDropRef } = useDroppable({
		id: nestDropZoneId,
		data: {
			acceptsPage: true,
			nodeType: "folder-nest-target",
			containerId: node.RowId,
		},
	});

	const style = {
		opacity: isDragging ? 0.85 : 1,
		transform: CSS.Transform.toString(transform),
		transition,
	};
	const isOverNest = isOverNestState || isNestTarget;
	const hasChildren = childIds.length > 0;
	const isCollapsible = hasChildren;
	const childrenCollapsed = isCollapsible ? isCollapsed : false;

	const className = [
		"infopage__row",
		"infopage__row--folder",
		isOverNest ? "infopage__row--nest-target" : "",
		isOver && !isOverNest ? "infopage__row--reorder-target" : "",
		isDragging ? "infopage__row--dragging" : "",
	]
		.filter(Boolean)
		.join(" ");

	const primaryActions = canAdmin
		? [
				...(onCreateChildPage
					? [
							{
								label: "Create page in folder",
								icon: "plus-circle",
								onSelect: () => onCreateChildPage(node.RowId),
							},
						]
					: []),
				{ label: "Rename folder", icon: "pencil", onSelect: onEditFolder },
				{
					label: "Delete folder",
					icon: "trash",
					onSelect: onDeleteNode,
				},
			]
		: [];

	const folderIcon =
		!hasChildren || childrenCollapsed ? "folder" : "folder-open";

	return (
		<div ref={setNodeRef} style={style} className={className}>
			<div className="folder__body" ref={setNestDropRef}>
				<div className="folder__header">
					<div
						className="row__drag-handle"
						{...listeners}
						{...attributes}
						data-node-type="folder"
						data-node-parent="root"
						data-dragging={isDragging ? "true" : undefined}
					>
						<RowGripIcon />
					</div>
					<div className="row__content">
						{hasChildren ? (
							<button
								type="button"
								className="folder__summary"
								onClick={onToggleCollapse}
								aria-expanded={!childrenCollapsed}
							>
								<div className="folder__summary-main">
									<FontAwesome name={folderIcon} className="row__type-icon" />
									<span className="row__title">{node.title}</span>
									<FontAwesome
										name="chevron-right"
										className={`folder__chevron ${childrenCollapsed ? "" : "folder__chevron--open"}`}
									/>
								</div>
								<span className="row__meta">
									{childIds.length} page{childIds.length > 1 ? "s" : ""}
								</span>
							</button>
						) : (
							<div className="folder__summary folder__summary--static">
								<div className="folder__summary-main">
									<FontAwesome name={folderIcon} className="row__type-icon" />
									<span className="row__title">{node.title}</span>
								</div>
								<span className="row__meta">Empty</span>
							</div>
						)}
					</div>
					{primaryActions.length ? (
						<RowActionsMenu
							label={`More actions for ${node.title}`}
							items={primaryActions}
						/>
					) : null}
				</div>

				<SortableContext
					items={childIds}
					strategy={verticalListSortingStrategy}
				>
					<div
						className={[
							"folder__children",
							childrenCollapsed ? "folder__children--collapsed" : "",
						]
							.filter(Boolean)
							.join(" ")}
						aria-hidden={childrenCollapsed}
					>
						{childIds.length === 0 ? (
							<p className="folder__empty-hint">
								&quot;{node.title}&quot; is empty. You can add pages to by
								dropping them here
							</p>
						) : null}
						{children}
					</div>
				</SortableContext>
			</div>
		</div>
	);
}
